/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountsApiService } from '../../modules/centralisedManagement/shared/http';
import { Component } from '@angular/core';
import {
    DownloadService,
    KeyStoreService,
    LogService,
    NotifyService,
    PGPService,
    UtilsService
} from '../../core/services';
import { ErrorResponseModel } from '../../core/models';
import { KeyStoreEnum } from '../../core/enums';
import { NavigationExtras, Router } from '@angular/router';
import { Observable, Subject, Subscriber } from 'rxjs';
import { complexPasswordValidation } from '../../core/directives/validation';
import { concatMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-login',
    templateUrl: './password-recovery.component.html',
    styleUrls: ['../externalPageStyles.scss', 'password-recovery.component.scss'],
})

export class PasswordRecoveryComponent {
    /**
     * Store the current step.
     */
    public activeStep: number = 1;

    /**
     * Password recovery form.
     */
    public passwordRecovery: FormGroup;

    /**
     * Store the file information to change the view.
     */
    public file: any = {
        user: null
    }
    ;
    /**
     * State of the file input to change the view.
     */
    public inputReset: any = {
        user: false
    };

    /**
     * Filename for key.
     */
    public privateKeyName: string = 'lbUserPrivate.key';

    /**
     * Store user details for redirect to login.
     * @private
     */
    private storedUserDetails = {
        email: null,
        password: null
    };

    /**
     * As we are not extending base we need reference to destroy.
     * @protected
     */
    protected destroyed$: Subject<boolean> = new Subject();

    constructor(
        private accountsApiService: AccountsApiService,
        private downloadService: DownloadService,
        private formBuilder: FormBuilder,
        private keyStoreService: KeyStoreService,
        private logService: LogService,
        private notifyService: NotifyService,
        private pgpService: PGPService,
        private router: Router,
        private utilsService: UtilsService
    ) {

        this.setUpFormGroups();
        this.setUpFormSubscribers();
    }

    /**
     * Force 'click' the file input to show the file browser
     * @param event
     */
    public openFileBrowser(event: any): void {
        event.preventDefault();
        const uploadButton = this.utilsService.getObjectValue(event.target, 'nextSibling.childNodes.0.childNodes.0');
        if (event.target.className.includes('button-key') && uploadButton) {
            uploadButton.click();
        }
    }

    /**
     * Read the file & check the extension type to ensure it is correct.
     * @param event
     * @param formFieldTag
     */
    public onFileChange(event: any, formFieldTag: string | number): void {
        this.inputReset[formFieldTag] = false;
        const fileList: FileList = event.target.files;

        if (!fileList.length) {
            this.notifyService.error('No file retrieved from the browser event');
        } else {
            this.file[formFieldTag] = fileList[0];
            const reader: FileReader = new FileReader();
            reader.readAsText(fileList[0], 'UTF-8');
            reader.onload = () => this.onReaderLoad(reader, formFieldTag);
        }
    }

    /**
     * Clear selected upload.
     */
    public clearUpload(formFieldTag: string): void {
        this.file[formFieldTag] = null;
        this.inputReset[formFieldTag] = true;
        this.passwordRecovery.get(`step1.${formFieldTag}PrivateKey`).setValue(null);
    }

    /**
     * Move to the next step.
     */
    public nextStep(): void {
        this.activeStep++;
    }

    /**
     * Set private key as true in form.
     * @decryptedKey
     */
    public downloadKeyAndValidate(decryptedKey: { armor: () => BlobPart; }): void {
        this.downloadService.generateDownload(
            decryptedKey.armor(), this.privateKeyName, { callBack: this.downloadComplete.bind(this) }
        );
    }

    /**
     * Download complete helper function.
     */
    public downloadComplete(): void {
        this.passwordRecovery.get('step4.keyDownloaded').setValue(true);
    }

    /**
     * Update user password helper function.
     */
    public updatePassword(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const formValues = this.passwordRecovery.value;
            this.generateNewUserKeysWithNewPassword(formValues)
                .pipe(
                    concatMap(
                        ({
                             formValues,
                             userEncryptedPrivateKey
                         }) => this.decryptNewUserPrivateKey(formValues, userEncryptedPrivateKey)),
                    concatMap(({ formValues }) => this.submitRecovery(formValues))
                )
                .subscribe({
                    next: (): void => {
                        this.nextStep();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.activeStep = 1;
                        this.logService.error('PasswordRecoveryComponent: updatePassword', error.message);
                        this.notifyService.error('There was an issue recovering your password. ' + error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Log user in.
     */
    public logUserIn(): void {
        const navigationExtras: NavigationExtras = {
            state: {
                email: this.storedUserDetails.email,
                password: this.storedUserDetails.password,
            }
        };

        this.router.navigateByUrl('/login', navigationExtras).then().catch();
    }

    /**
     * Generate new keys for the user with their new password.
     * @param formValues
     * @private
     */
    private generateNewUserKeysWithNewPassword(formValues: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const userData: ({ email: string }) = { email: formValues.step1.email };
            this.pgpService.generateNewUserKeys(userData, formValues.step2.password)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: ({ armoredKeys: armoredUsersKeys, hashRemainder }): void => {
                        this.keyStoreService.storeState = KeyStoreEnum.OPEN;
                        this.keyStoreService.userPrivateKey = armoredUsersKeys.privateKey;

                        formValues.step3.newUserPublicKey = armoredUsersKeys.publicKey;
                        formValues.step3.newUserPrivateKey = armoredUsersKeys.privateKey;
                        formValues.step3.hashedPassword = hashRemainder;

                        const userEncryptedPrivateKey = armoredUsersKeys.privateKey;

                        observer.next({ formValues, userEncryptedPrivateKey });
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('PasswordRecoveryComponent: generateNewUserKeysWithNewPassword', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Decrypt user's private key.
     * @param formValues
     * @param userEncryptedPrivateKey
     * @private
     */
    private decryptNewUserPrivateKey(formValues: any, userEncryptedPrivateKey: string): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.pgpService.decryptPrivateKeyWithPassword(formValues.step2.password, userEncryptedPrivateKey, true)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (decryptedUserKey): void => {
                        observer.next({ formValues, decryptedUserKey });
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('PasswordRecoveryComponent: decryptPrivateKey', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Submit the details to the API.
     * @param formValues
     * @private
     */
    private submitRecovery(formValues: { step1: any; step2: any; step3: any; },): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const submitValues = { ...formValues.step1, ...formValues.step2, ...formValues.step3 };
            // Store the user details to pass to the login page, do this before manipulating the data.
            this.storedUserDetails = {
                email: submitValues.email,
                password: submitValues.password,
            };

            submitValues.password = submitValues.hashedPassword;

            delete submitValues.confirmPassword;
            delete submitValues.hashedPassword;
            delete submitValues.userPrivateKey;

            this.accountsApiService.recoverPassword(submitValues)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('PasswordRecoveryComponent: encryptOrgKeyWithUserKey', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * File loaded callback function.
     * @param reader
     * @param formFieldTag
     * @private
     */
    private onReaderLoad(reader: FileReader, formFieldTag: string | number): void {
        this.passwordRecovery.get(`step1.${formFieldTag}PrivateKey`).setValue(reader.result);
        this.signMessageWithPrivateKey('Loadbalancer password recovery message.', reader.result, formFieldTag);
    }

    /**
     * Sign a message with the private key.
     * @param message
     * @param privateKey
     * @param formFieldTag
     * @private
     */
    private signMessageWithPrivateKey(message: string, privateKey: string | ArrayBuffer, formFieldTag: string | number): void {
        this.pgpService.signClearMessageWithPrivateKey(message, privateKey)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (signedMessage: any): void => {
                    this.passwordRecovery.get(`step1.${formFieldTag}Signature`).setValue(signedMessage);
                },
                error: (error: ErrorResponseModel): void => {
                    this.notifyService.error('There was an Issue reading your key. Please select a Different key and try again');
                    this.logService.error('PasswordRecoveryComponent: encryptMessage', error.message);
                }
            });
    }

    /**
     * Setup page form groups.
     * @private
     */
    private setUpFormGroups(): void {
        // Set up our forms
        this.passwordRecovery = this.formBuilder.group({
            step1: this.formBuilder.group({
                email: [null, [Validators.required, Validators.email]],
                userPrivateKey: [null, Validators.required],
                userSignature: [null, Validators.required]
            }),
            step2: this.formBuilder.group({
                password: [null, [Validators.required, complexPasswordValidation]],
                confirmPassword: [null, [Validators.required, complexPasswordValidation]],
            }),
            step3: this.formBuilder.group({
                hashedPassword: [null, Validators.required],
                newUserPublicKey: [null, Validators.required],
                newUserPrivateKey: [null, Validators.required]
            }),
            step4: this.formBuilder.group({
                keyDownloaded: [null, Validators.required],
            }),
        });
    }

    /**
     * Setup form subscribers
     * @private
     */
    private setUpFormSubscribers(): void {
        const confirmPasswordInput: AbstractControl = this.passwordRecovery.get('step2.confirmPassword');
        confirmPasswordInput.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: confirmPassword => {
                    // Don't use debounce here as it will cause the submit button to flash.
                    const password = this.passwordRecovery.get('step2.password').value;
                    this.utilsService.validatePasswordWithConfirmPassword(password, confirmPassword, confirmPasswordInput);
                }
            });
    }
}
